import * as THREE from 'three';
import { damp, DEG2RAD, lerp } from '../core/math';
import type { Input } from '../core/Input';
import { CapsuleWorld, type CapsuleBody } from '../physics/CapsuleWorld';

export interface PlayerConfig {
  walkSpeed: number;
  sprintSpeed: number;
  crouchSpeed: number;
  accelGround: number;
  accelAir: number;
  friction: number;
  jumpSpeed: number;
  mouseSensitivity: number;
  standingHeight: number;
  crouchHeight: number;
  slideHeight: number;
  slideDuration: number;
  slideBoost: number;
  /** Extra horizontal speed applied when jumping out of a slide. */
  slideJumpBoost: number;
  radius: number;
  eyeOffset: number;
  fov: number;
  killY: number;
}

const DEFAULT_PLAYER: PlayerConfig = {
  walkSpeed: 6.2,
  sprintSpeed: 10.5,
  crouchSpeed: 3.2,
  accelGround: 55,
  accelAir: 18,
  friction: 28,
  jumpSpeed: 9.2,
  mouseSensitivity: 0.085,
  standingHeight: 1.8,
  crouchHeight: 1.15,
  slideHeight: 0.85,
  slideDuration: 0.85,
  slideBoost: 1.35,
  slideJumpBoost: 1.22,
  radius: 0.35,
  eyeOffset: 0.12,
  fov: 85,
  killY: -8,
};

export class PlayerController {
  readonly camera: THREE.PerspectiveCamera;
  readonly body: CapsuleBody;
  readonly config: PlayerConfig;

  yaw = 0;
  pitch = 0;

  crouchBlend = 0;
  bobPhase = 0;
  bobStrength = 0;

  /** 0..1 visual blend while slide/glide is active. */
  slideBlend = 0;
  /** True while a slide is in progress. */
  isSliding = false;
  /** -1..1 lean (Q left, E right). */
  lean = 0;
  /** While > 0, sprint is suppressed (e.g. hip-fire from run). */
  sprintSuppressT = 0;

  health = 100;
  maxHealth = 100;
  armor = 0;
  maxArmor = 100;

  /** World point the last damaging hit came from (for the HUD wedge). */
  readonly lastHitFrom = new THREE.Vector3();
  hasLastHit = false;
  damageFlashT = 0;

  /** Multiplier applied to mouse look (ADS tightens this). */
  lookSensitivityScale = 1;

  private baseFov: number;
  private adsFovTarget = 70;
  private adsFovBlend = 0;
  private recoilPitch = 0;
  private slideT = 0;
  private readonly slideDir = new THREE.Vector3();
  private wasSprinting = false;

  private spawnPos = new THREE.Vector3();
  private spawnYaw = 0;
  private spawnCrouch = false;
  private climbT = 0;
  private climbDur = 0;
  private climbCrouch = false;
  private readonly climbFrom = new THREE.Vector3();
  private readonly climbTo = new THREE.Vector3();
  private stepAcc = 0;
  private static readonly STEP_SPACING = 1.32;
  private blastShakeT = 0;
  private blastShakeAmp = 0;
  private blastPhase = 0;
  private static readonly BLAST_SHAKE_DUR = 0.36;

  private readonly world: CapsuleWorld;
  private readonly wish = new THREE.Vector3();
  private readonly forward = new THREE.Vector3();
  private readonly right = new THREE.Vector3();

  constructor(world: CapsuleWorld, spawn: THREE.Vector3, config: Partial<PlayerConfig> = {}) {
    this.world = world;
    this.config = { ...DEFAULT_PLAYER, ...config };
    this.baseFov = this.config.fov;

    this.camera = new THREE.PerspectiveCamera(this.config.fov, 1, 0.05, 400);
    this.camera.rotation.order = 'YXZ';

    this.body = {
      position: spawn.clone(),
      velocity: new THREE.Vector3(),
      radius: this.config.radius,
      height: this.config.standingHeight,
      grounded: false,
      groundNormal: new THREE.Vector3(0, 1, 0),
    };

    this.setSpawn(spawn, 0);
    this.syncCamera(0);
  }

  /** Push out of a door leaf that just became solid. */
  unstick(): void {
    this.world.unstick(this.body);
  }

  setFovBlend(adsBlend: number, adsFov: number): void {
    this.adsFovBlend = adsBlend;
    this.adsFovTarget = adsFov;
  }

  addBlastShake(origin: THREE.Vector3, radius: number): void {
    const dist = this.camera.position.distanceTo(origin);
    const near = Math.max(1.15, radius * 0.3);
    const far = 26;
    let fall = 0;
    if (dist <= near) fall = 1;
    else if (dist < far) fall = 1 - (dist - near) / (far - near);
    if (fall < 0.04) return;
    const amp = (0.034 + Math.min(radius, 7) * 0.0022) * fall;
    this.blastShakeAmp = Math.max(this.blastShakeAmp, amp);
    this.blastShakeT = Math.max(this.blastShakeT, PlayerController.BLAST_SHAKE_DUR);
  }

  /** Decay blast rumble. Returns true if the camera still needs a sync this frame. */
  updateShake(dt: number): boolean {
    if (this.blastShakeT <= 0 && this.blastShakeAmp <= 0) return false;
    this.blastShakeT = Math.max(0, this.blastShakeT - dt);
    this.blastPhase += dt;
    if (this.blastShakeT <= 0) this.blastShakeAmp = 0;
    return true;
  }

  addRecoilPunch(amount: number): void {
    // Tiny camera nudge only — most feedback is viewmodel kick, not look direction
    const lookKick = amount * 0.08;
    this.recoilPitch += lookKick;
    this.pitch = Math.max(-89 * DEG2RAD, this.pitch - lookKick);
    this.camera.fov = Math.min(this.baseFov + 2.5, this.camera.fov + amount * 8);
    this.camera.updateProjectionMatrix();
  }

  update(input: Input, dt: number): void {
    if (!input.isLocked) return;

    const mouse = input.consumeMouseDelta();
    const sens = this.config.mouseSensitivity * DEG2RAD * this.lookSensitivityScale;
    this.yaw -= mouse.x * sens;
    this.pitch -= mouse.y * sens;
    const pitchLimit = 89 * DEG2RAD;
    this.pitch = Math.max(-pitchLimit, Math.min(pitchLimit, this.pitch));

    // Recoil recovery on camera punch buffer
    this.recoilPitch = damp(this.recoilPitch, 0, 10, dt);

    // Q/E lean — reduced while sliding/ADS-heavy feels better if kept available always
    const leanTarget = this.isSliding ? 0 : input.leanAxis;
    this.lean = damp(this.lean, leanTarget, 12, dt);

    this.forward.set(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
    this.right.set(Math.cos(this.yaw), 0, -Math.sin(this.yaw));

    if (this.climbT > 0) {
      this.tickClimb(dt);
      return;
    }

    if (this.sprintSuppressT > 0) this.sprintSuppressT = Math.max(0, this.sprintSuppressT - dt);

    const axis = input.moveAxis;
    const hSpeedNow = Math.hypot(this.body.velocity.x, this.body.velocity.z);
    const sprinting =
      input.sprinting &&
      !input.crouching &&
      !this.isSliding &&
      this.sprintSuppressT <= 0 &&
      axis.y > 0.1 &&
      this.body.grounded;

    // Sprint + crouch press → short glide/slide
    if (
      !this.isSliding &&
      this.body.grounded &&
      input.crouchPressed &&
      (this.wasSprinting || (input.sprinting && hSpeedNow > this.config.walkSpeed * 0.9))
    ) {
      this.beginSlide();
    }
    this.wasSprinting = sprinting || (input.sprinting && axis.y > 0.1 && !this.isSliding);

    if (this.isSliding) {
      this.slideT -= dt;
      const life = Math.max(0, this.slideT / this.config.slideDuration);
      this.slideBlend = damp(this.slideBlend, life > 0 ? Math.min(1, life * 1.4) : 0, 14, dt);
      this.crouchBlend = damp(this.crouchBlend, 1, 18, dt);
      this.body.height = lerp(this.config.standingHeight, this.config.slideHeight, this.crouchBlend);

      // Lock glide along slide direction with light steering
      const steer = this.right.clone().multiplyScalar(axis.x * 0.35);
      const glide = this.slideDir.clone().add(steer);
      if (glide.lengthSq() > 1e-6) glide.normalize();
      const glideSpeed = this.config.sprintSpeed * this.config.slideBoost * Math.max(0.35, life);
      this.body.velocity.x = glide.x * glideSpeed;
      this.body.velocity.z = glide.z * glideSpeed;

      // Jump cancel anytime during slide → hop with bonus speed along slide dir
      if (input.jumpPressed && this.body.grounded) {
        this.endSlideJump(glide);
      } else if (this.slideT <= 0 || !this.body.grounded) {
        this.isSliding = false;
        this.slideT = 0;
      }
    } else {
      this.slideBlend = damp(this.slideBlend, 0, 12, dt);
      let wantCrouch = input.crouching;
      // Stay forced-crouched under low ceilings / desks until standing height clears
      if (!wantCrouch && this.crouchBlend > 0.08) {
        if (!this.world.canFitHeight(this.body, this.config.standingHeight)) {
          wantCrouch = true;
        }
      }
      this.crouchBlend = damp(this.crouchBlend, wantCrouch ? 1 : 0, wantCrouch ? 14 : 10, dt);
      // Never grow taller than what fits right now (avoids a one-frame head clip)
      let targetH = lerp(this.config.standingHeight, this.config.crouchHeight, this.crouchBlend);
      if (
        targetH > this.body.height + 0.01 &&
        !this.world.canFitHeight(this.body, targetH)
      ) {
        targetH = this.body.height;
        this.crouchBlend = 1;
      }
      this.body.height = targetH;

      this.wish
        .set(0, 0, 0)
        .addScaledVector(this.forward, axis.y)
        .addScaledVector(this.right, axis.x);
      if (this.wish.lengthSq() > 1e-8) this.wish.normalize();

      let maxSpeed = this.config.walkSpeed;
      if (wantCrouch || this.crouchBlend > 0.5) maxSpeed = this.config.crouchSpeed;
      else if (sprinting) maxSpeed = this.config.sprintSpeed;

      const accel = this.body.grounded ? this.config.accelGround : this.config.accelAir;
      this.applyAcceleration(this.wish, maxSpeed, accel, dt);

      if (this.body.grounded) {
        this.applyFriction(dt, axis.x !== 0 || axis.y !== 0);
      }

      if (input.jumpPressed && this.body.grounded && this.crouchBlend < 0.5) {
        this.body.velocity.y = this.config.jumpSpeed;
        this.body.grounded = false;
      }
    }

    this.world.move(this.body, dt);

    // Kill plane — never fall forever
    if (this.body.position.y < this.config.killY) {
      this.respawn();
    }

    const hSpeed = Math.hypot(this.body.velocity.x, this.body.velocity.z);
    const bobTarget =
      this.body.grounded && !this.isSliding ? Math.min(1, hSpeed / this.config.sprintSpeed) : 0;
    this.bobStrength = damp(this.bobStrength, bobTarget, 8, dt);
    this.bobPhase += dt * (8 + hSpeed * 1.2) * this.bobStrength;
    const ventCrawl = this.body.position.y > 2.85 && this.body.position.y < 4.6;
    if (this.body.grounded && !this.isSliding && (this.crouchBlend <= 0.5 || ventCrawl) && hSpeed > 0.7)
      this.stepAcc += hSpeed * dt;
    else this.stepAcc = 0;

    // FOV
    this.camera.fov = lerp(this.baseFov, this.adsFovTarget, this.adsFovBlend);
    this.camera.updateProjectionMatrix();

    this.syncCamera(dt);
  }

  private applyAcceleration(wishDir: THREE.Vector3, wishSpeed: number, accel: number, dt: number): void {
    if (wishDir.lengthSq() < 1e-8) return;
    const currentSpeed = this.body.velocity.x * wishDir.x + this.body.velocity.z * wishDir.z;
    const addSpeed = wishSpeed - currentSpeed;
    if (addSpeed <= 0) return;
    let accelSpeed = accel * dt * wishSpeed;
    if (accelSpeed > addSpeed) accelSpeed = addSpeed;
    this.body.velocity.x += accelSpeed * wishDir.x;
    this.body.velocity.z += accelSpeed * wishDir.z;
  }

  private applyFriction(dt: number, hasInput: boolean): void {
    const speed = Math.hypot(this.body.velocity.x, this.body.velocity.z);
    if (speed < 1e-4) {
      this.body.velocity.x = 0;
      this.body.velocity.z = 0;
      return;
    }
    const friction = hasInput ? this.config.friction * 0.45 : this.config.friction;
    const drop = speed * friction * dt;
    const newSpeed = Math.max(0, speed - drop);
    const scale = newSpeed / speed;
    this.body.velocity.x *= scale;
    this.body.velocity.z *= scale;
  }

  private beginSlide(): void {
    this.isSliding = true;
    this.slideT = this.config.slideDuration;
    this.slideBlend = 1;
    // Prefer current velocity direction; fall back to look forward
    this.slideDir.set(this.body.velocity.x, 0, this.body.velocity.z);
    if (this.slideDir.lengthSq() < 0.25) this.slideDir.copy(this.forward);
    else this.slideDir.normalize();
    const boost = this.config.sprintSpeed * this.config.slideBoost;
    this.body.velocity.x = this.slideDir.x * boost;
    this.body.velocity.z = this.slideDir.z * boost;
  }

  private endSlideJump(dir: THREE.Vector3): void {
    const jumpDir = dir.lengthSq() > 1e-6 ? dir.clone().normalize() : this.slideDir.clone();
    if (jumpDir.lengthSq() < 1e-6) jumpDir.copy(this.forward);
    // Keep horizontal speed = current glide speed (no extra boost)
    const speed = this.config.sprintSpeed * this.config.slideBoost;
    this.body.velocity.x = jumpDir.x * speed;
    this.body.velocity.z = jumpDir.z * speed;
    this.body.velocity.y = this.config.jumpSpeed;
    this.body.grounded = false;
    this.isSliding = false;
    this.slideT = 0;
    this.slideBlend = 0.35; // quick visual settle
    this.crouchBlend = 0.2;
    this.body.height = lerp(this.config.standingHeight, this.config.crouchHeight, this.crouchBlend);
  }

  private syncCamera(_dt: number): void {
    const eyeHeight = this.body.height - this.config.eyeOffset;
    const bobY = Math.sin(this.bobPhase) * 0.025 * this.bobStrength * (1 - this.slideBlend);
    const bobX = Math.cos(this.bobPhase * 0.5) * 0.012 * this.bobStrength * (1 - this.slideBlend);
    // Slight camera dip while sliding
    const slideDip = this.slideBlend * 0.08;
    const leanOffset = 0.28 * this.lean;
    const leanRoll = -0.22 * this.lean;

    this.camera.position.set(
      this.body.position.x + this.right.x * (bobX + leanOffset),
      this.body.position.y + eyeHeight + bobY - slideDip,
      this.body.position.z + this.right.z * (bobX + leanOffset),
    );
    this.camera.rotation.order = 'YXZ';
    this.camera.rotation.y = this.yaw;
    this.camera.rotation.x = this.pitch - this.slideBlend * 0.08;
    this.camera.rotation.z = leanRoll;
    this.applyBlastShake();
  }

  private applyBlastShake(): void {
    if (this.blastShakeT <= 0) return;
    const u = this.blastShakeT / PlayerController.BLAST_SHAKE_DUR;
    const a = this.blastShakeAmp * u * u;
    const t = this.blastPhase;
    this.camera.position.x += Math.sin(t * 58) * a;
    this.camera.position.y += Math.sin(t * 73 + 0.8) * a * 0.62;
    this.camera.position.z += Math.cos(t * 51) * a;
    this.camera.rotation.z += Math.sin(t * 42) * a * 0.38;
  }

  /** For Playwright calib when pointer-lock is unavailable. */
  syncCameraOnly(): void {
    this.forward.set(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
    this.right.set(Math.cos(this.yaw), 0, -Math.sin(this.yaw));
    this.syncCamera(0);
  }

  /** Teleport + aim for enemy weapon screenshots. */
  placeForCalib(x: number, y: number, z: number, yaw: number, pitch = 0): void {
    this.body.position.set(x, y, z);
    this.body.velocity.set(0, 0, 0);
    this.yaw = yaw;
    this.pitch = pitch;
    this.isSliding = false;
    this.slideBlend = 0;
    this.lean = 0;
    this.bobStrength = 0;
    this.syncCameraOnly();
  }

  /** Apply damage through armor first. Returns true if the player died. */
  takeDamage(amount: number, from?: THREE.Vector3): boolean {
    if (amount <= 0 || this.health <= 0) return this.health <= 0;
    let left = amount;
    if (this.armor > 0) {
      const absorbed = Math.min(this.armor, left * 0.65);
      this.armor -= absorbed;
      left -= absorbed;
    }
    this.health = Math.max(0, this.health - left);
    this.damageFlashT = 0.58;
    if (from) {
      this.lastHitFrom.copy(from);
      this.hasLastHit = true;
    }
    if (this.health <= 0) {
      this.respawn();
      return true;
    }
    return false;
  }

  /** @returns false if already at max */
  heal(amount: number): boolean {
    if (this.health >= this.maxHealth) return false;
    this.health = Math.min(this.maxHealth, this.health + amount);
    return true;
  }

  /** @returns false if already at max */
  addArmor(amount: number): boolean {
    if (this.armor >= this.maxArmor) return false;
    this.armor = Math.min(this.maxArmor, this.armor + amount);
    return true;
  }

  get isClimbing(): boolean {
    return this.climbT > 0;
  }

  startClimb(dest: THREE.Vector3, opts: { crouch?: boolean; duration?: number } = {}): void {
    this.climbFrom.copy(this.body.position);
    this.climbTo.copy(dest);
    this.climbDur = Math.max(0.12, opts.duration ?? 0.42);
    this.climbT = this.climbDur;
    this.climbCrouch = !!opts.crouch;
    this.body.velocity.set(0, 0, 0);
    this.isSliding = false;
    this.slideT = 0;
    this.slideBlend = 0;
  }

  private tickClimb(dt: number): void {
    this.climbT = Math.max(0, this.climbT - dt);
    const u = this.climbDur > 1e-6 ? 1 - this.climbT / this.climbDur : 1;
    const e = u * u * (3 - 2 * u);
    this.body.position.lerpVectors(this.climbFrom, this.climbTo, e);
    this.body.velocity.set(0, 0, 0);
    this.body.grounded = this.climbT <= 0;
    if (this.climbCrouch) {
      this.crouchBlend = 1;
      this.body.height = this.config.crouchHeight;
    }
    this.syncCamera(dt);
  }

  setSpawn(pos: THREE.Vector3, yaw = 0, opts: { crouch?: boolean } = {}): void {
    this.spawnPos.copy(pos);
    this.spawnYaw = yaw;
    this.spawnCrouch = !!opts.crouch;
    this.respawn();
  }

  respawn(): void {
    this.body.position.copy(this.spawnPos);
    this.body.velocity.set(0, 0, 0);
    this.yaw = this.spawnYaw;
    this.pitch = 0;
    this.recoilPitch = 0;
    this.isSliding = false;
    this.slideT = 0;
    this.slideBlend = 0;
    this.lean = 0;
    this.crouchBlend = this.spawnCrouch ? 1 : 0;
    this.body.height = this.spawnCrouch ? this.config.crouchHeight : this.config.standingHeight;
    this.health = this.maxHealth;
    this.armor = 0;
    this.damageFlashT = 0;
    this.hasLastHit = false;
    this.blastShakeT = 0;
    this.blastShakeAmp = 0;
    this.stepAcc = 0;
    this.climbT = 0;
    this.syncCamera(0);
  }

  /** True once per footfall while grounded and moving. */
  consumeStep(): boolean {
    if (this.stepAcc < PlayerController.STEP_SPACING) return false;
    this.stepAcc -= PlayerController.STEP_SPACING;
    return true;
  }
}
